import { t } from 'i18next'
import React from 'react'

import type { ClientModel } from 'state/client'

import type { FarmOptionModel } from 'state/farm'
import { jwtDecode } from 'jwt-decode'

import type { Subscription, SubscriptionPeriod } from 'state/account'
import { subscriptionStatus } from '@farmtrx/options'
import { DeviceType, isDeviceType } from '@farmtrx/devices'

export type JWTTokenData = {
  [key: string]: any
  iss?: string | undefined
  sub?: string | undefined
  aud?: string | string[] | undefined
  exp?: number | undefined
  nbf?: number | undefined
  iat?: number | undefined
  jti?: string | undefined
}
export type ClientFarmResult = {
  client: ClientModel
  farms: FarmOptionModel[]
}
const yieldKeys = {
  tonnes_ha: ['yldt_ha', 'YLDt_ha'],
  bu_ac: ['yldbu_ac', 'YLDbu_ac'],
  kg_ha: ['yldkg_ha', 'YLDkg_ha'],
}

/**
 * Checks if as string is blank
 *
 * @param {string} str
 * @returns {boolean} true if blank (null, undefined or with spaces).
 */
export function isBlank(str: string): boolean {
  return !str || /^\s*$/.test(str)
}

export function isNotBlank(str: string): boolean {
  return !isBlank(str)
}

export function str(str: string): string {
  return JSON.stringify(str, null, 2)
}

export function noop() {}

export function hexToRgba(hex: string, opacity: number = 1): string {
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    let c: string[] | string = hex.substring(1).split('')
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]]
    }
    c = '0x' + c.join('')
    const numericVal = parseInt(c)
    return `rgba(${[
      (numericVal >> 16) & 255,
      (numericVal >> 8) & 255,
      numericVal & 255,
    ].join(',')}, ${opacity})`
  }

  throw new Error('Bad Hex')
}

export function toDateString(date: Date): string {
  return (
    ('0' + date.getDate()).slice(-2) +
    '-' +
    ('0' + (date.getMonth() + 1)).slice(-2) +
    '-' +
    date.getFullYear()
  )
}

export function getPartner(): string {
  return window.location.host.split('.')[1]
    ? window.location.host.split('.')[0]
    : 'farmtrx'
}

// Each deployment's logo is uploaded to the assets bucket as <subdomain>.png,
// including the FarmTRX-branded ones (web, eu, staging, mydom, farmtrx).
export function getPartnerLogoUrl(): string {
  return `${PARTNER_ASSETS_BASE_URL}/${getPartner()}.png`
}

// Optional dark-background variant, uploaded as <subdomain>-reverse.png
// alongside the base logo. Not every deployment has one; callers fall back
// to getPartnerLogoUrl() when this 404s.
export function getPartnerReverseLogoUrl(): string {
  return `${PARTNER_ASSETS_BASE_URL}/${getPartner()}-reverse.png`
}

export function getInitialUnits(isMetric: boolean): object {
  const units = {
    area_in: isMetric ? 'ha' : 'ac',
    length_in: isMetric ? 'meters' : 'feet',
    rate_in: isMetric ? 'ha_hr' : 'ac_hr',
    speed_in: isMetric ? 'kmh' : 'mph',
    temperature_in: isMetric ? 'celsius' : 'fahrenheit',
    testweight_in: isMetric ? 'kg_hl' : 'lb_usbu',
    totalyield_in: isMetric ? 'tonne' : 'bu',
    volume_in: isMetric ? 'cubic_meters' : 'bushels',
    weight_in: isMetric ? 'tonne' : 'tonsUS',
    yieldunit_in: isMetric ? 'tonnes_ha' : 'bu_ac',
  }
  return units
}

export function requestYieldUnit(preferredYield): string {
  const yieldUnit = yieldKeys[preferredYield]
  if (yieldUnit != null) {
    return yieldUnit[0]
  }
  return yieldKeys.tonnes_ha[0]
}

export function getTranslatedName(name: string): string {
  const tmp = name.toLowerCase().replace(' ', '_')
  const translatedName = t(tmp)
  //If we don't have a translation for it, return name
  return translatedName === tmp ? name : translatedName
}

export function getYearsWithHarvests(harvestYears): number[] {
  const filtered = harvestYears?.filter((hYear) => {
    if (hYear.hasHarvest) {
      return hYear.harvestYr
    }
  })
  if (!filtered?.length) {
    return [new Date().getFullYear()]
  }
  return filtered.map((hYear) => hYear.harvestYr)
}

export function useAppBarHeight(): number {
  const [appBarHeight, setAppBarHeight] = React.useState(0)

  React.useEffect(() => {
    const onResize: ResizeObserverCallback = ([{ target: appBar }]) => {
      appBar && setAppBarHeight(appBar.clientHeight)
    }

    const appBar = document.querySelector('header.MuiAppBar-root')
    if (!appBar) return

    const resizeObserver = new ResizeObserver(onResize)
    resizeObserver.observe(appBar)

    return () => resizeObserver.disconnect()
  }, [])

  return appBarHeight
}

export function getClientFarm(
  cffEnabled: boolean,
  clients: ClientModel[],
  prefDefault: string,
): {
  client: ClientModel | undefined
  farms: FarmOptionModel[]
  selectedFarms: FarmOptionModel[]
} {
  let client: ClientModel | undefined = undefined
  let selectedClient: ClientModel | undefined = undefined
  if (clients.length === 0) {
    return {
      client,
      farms: [],
      selectedFarms: [],
    }
  }
  if (cffEnabled) {
    const defaultClient = localStorage.getItem('selectedClient')
    if (
      defaultClient !== '' &&
      defaultClient !== 'undefined' &&
      defaultClient &&
      JSON.parse(defaultClient) !== ''
    ) {
      selectedClient = JSON.parse(defaultClient)
    }
    const freshClient =
      selectedClient && 'id' in selectedClient
        ? clients.find((c: ClientModel) => c.id === selectedClient?.id)
        : undefined
    if (freshClient) {
      // Always prefer the freshly-fetched client record (name, farms, etc.)
      // over the cached localStorage snapshot, which can go stale after a
      // client/farm rename since renames don't update the cached selection.
      client = freshClient
    } else if (clients.length > 0) {
      client = clients[0]
      localStorage.setItem('selectedClient', JSON.stringify(client))
    }
  } else if (clients.length) {
    if (prefDefault && typeof parseInt(prefDefault) === 'number') {
      client = clients.find((c) => c?.id === parseInt(prefDefault))
      if (client === undefined) {
        client = clients[0]
      }
      localStorage.setItem('selectedClient', JSON.stringify(client))
    } else {
      client = clients[0]
      localStorage.setItem('selectedClient', JSON.stringify(client))
    }
  }
  //Get Farm(s) from the above client
  const farms: FarmOptionModel[] = client?.farms || []
  let selectedFarms: FarmOptionModel[] = client?.farms || []
  if (cffEnabled) {
    const defaultFarms = localStorage.getItem('selectedFarms')
    if (defaultFarms !== '' && defaultFarms !== 'undefined' && defaultFarms) {
      // Re-resolve the cached farm ids against the fresh farm list rather
      // than trusting the cached farm objects themselves, which can be
      // stale or malformed (e.g. missing `name`) after a farm rename.
      const cachedFarms: FarmOptionModel[] = JSON.parse(defaultFarms)
      if (!cachedFarms.length) {
        // Preserve an explicit empty selection (cleared by user) so callers can
        // fall back to client-level filtering without enumerating farm IDs.
        selectedFarms = []
      } else {
        const cachedFarmIds = new Set(cachedFarms.map((f) => f?.id))
        const resolvedFarms = farms.filter((f) => cachedFarmIds.has(f.id))
        selectedFarms = resolvedFarms.length ? resolvedFarms : farms
      }
    } else {
      localStorage.setItem(
        'selectedFarms',
        farms?.length ? JSON.stringify(farms) : JSON.stringify([]),
      )
    }
  }
  //@ts-ignore
  return { client, farms, selectedFarms }
}

export function isTokenValid(token?: string, expiry: number = 0): boolean {
  const offset = 120 //seconds
  let tokenValid: boolean = false
  if (token && token !== '') {
    try {
      const decodedToken: JWTTokenData = jwtDecode(token)
      //Check to make sure token is valid for offset seconds from now, default is 2 minutes
      //can set longer for operations like harvest sync
      const now = Math.floor(Date.now() / 1000) + offset
      tokenValid = (decodedToken?.exp && decodedToken.exp > now) || false
    } catch (e) {
      if (expiry) {
        const now = Math.floor(Date.now() / 1000) + offset
        tokenValid = expiry > now
      } else {
        console.error('Error decoding token and no expiry date provided', e)
      }
    }
  }
  return tokenValid
}

export function isLegacySLD(lpVersion: string) {
  if (lpVersion !== undefined) {
    const [major, minor, patch] = lpVersion.split('.').map(Number)
    return (
      major < 5 ||
      (major <= 5 && minor < 3) ||
      (major <= 5 && minor <= 3 && patch < 1)
    )
  }
  return true
}

//Check if start date or end date are within a valid subscription
//start date and end date can be the same value (or just a year)
export function isSubscriptionValid(
  subscriptions: Subscription[],
  startDate: string,
  endDate: string,
): boolean {
  if (
    subscriptions &&
    subscriptions.length &&
    subscriptions[0]?.subscriptionPeriods &&
    startDate &&
    endDate
  ) {
    let subscriptionsFound: SubscriptionPeriod[] = []
    const subscriptionPeriods: SubscriptionPeriod[] =
      subscriptions[0].subscriptionPeriods
    subscriptionsFound = subscriptionPeriods.filter((subscription) => {
      return (
        (subscription.subscriptionStatus === subscriptionStatus.Paid ||
          subscription.subscriptionStatus === subscriptionStatus.FreeYear ||
          subscription.subscriptionStatus === subscriptionStatus.Comped) &&
        ((startDate >= subscription.startDate &&
          startDate <= subscription.endDate) ||
          (endDate <= subscription.endDate &&
            endDate >= subscription.startDate))
      )
    })
    if (subscriptionsFound && subscriptionsFound.length) {
      return true
    }
  }
  return false
}

// Translation key per firmware device type. Typed as a full Record so a device
// type added to @farmtrx/devices fails the build until it has a label here.
const DEVICE_LABEL_KEYS: Record<DeviceType, string> = {
  [DeviceType.UNKNOWN]: 'unknown',
  [DeviceType._LEGACY_YIELD_MONITOR_1]: 'legacy_yield_monitor',
  [DeviceType._LEGACY_MOISTURE_SENSOR_1]: 'legacy_moisture_sensor',
  [DeviceType._LEGACY_YIELD_MONITOR_2]: 'yield_monitor_2',
  [DeviceType.MOISTURE_SENSOR_2]: 'moisture_sensor_2',
  [DeviceType._UNUSED_YIELD_MONITOR_PRO]: 'yield_monitor_pro',
  [DeviceType.RTK_BASE_STATION_1]: 'rtk_base_station',
  [DeviceType._UNUSED_ADSB_RX]: 'adsb_receiver',
  [DeviceType._UNUSED_YIELD_SIMULATOR]: 'yield_simulator',
  [DeviceType.RADAR_SENSOR_1]: 'radar_sensor',
  [DeviceType._LEGACY_YIELD_MONITOR_3]: 'ecu3',
  [DeviceType.MOISTURE_SENSOR_3A]: 'moisture_sensor_3a',
  [DeviceType.YIELD_MONITOR_4V1]: 'ecu4',
  [DeviceType.YIELD_MONITOR_4V2]: 'ecu4',
  [DeviceType.HEADER_HEIGHT_SENSOR_1]: 'header_angle_sensor',
  [DeviceType.MOISTURE_SENSOR_3B]: 'moisture_sensor_3b',
  [DeviceType.YIELD_MONITOR_4V2_CG]: 'yield_monitor_4v2_cg',
  [DeviceType.YIELD_MONITOR_4V2_G]: 'yield_monitor_4v2_g',
  [DeviceType.NIR_SENSOR_1]: 'nir_sensor_1',
  [DeviceType.RTK_DATA_MODEM_1]: 'rtk_data_modem_1',
  [DeviceType.CAN_LOGGER_1]: 'can_logger_1',
  [DeviceType.YIELD_MONITOR_4V2_LITE]: 'yield_monitor_4v2_lite',
  [DeviceType.RTK_ROVER_1]: 'rtk_rover_1',
  [DeviceType.MOISTURE_SENSOR_5]: 'moisture_sensor_5',
  [DeviceType.RTK_BASE_STATION_1_EU]: 'rtk_base_station_eu',
  [DeviceType.ESP_KVM_DEVKITC_1]: 'esp_kvm_devkitc_1',
}

export function getDeviceString(
  deviceType: number,
  hardwareVersion?: number | string,
) {
  let dt = deviceType
  // handle special case for MS3a
  if (
    deviceType === DeviceType.MOISTURE_SENSOR_2 &&
    hardwareVersion &&
    Number(hardwareVersion) === 11
  ) {
    dt = DeviceType.MOISTURE_SENSOR_3A
  }
  return t(isDeviceType(dt) ? DEVICE_LABEL_KEYS[dt] : 'unknown')
}
